import { useState } from 'react';
import { Button } from '@/components/ui/button';
import IsIsntCard from '@/components/ethos/IsIsntCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  User, Building2, Shield, Briefcase, Loader2, 
  Eye, EyeOff, ChevronRight, ChevronLeft, CheckCircle2, Info
} from 'lucide-react';
import { toast } from 'sonner';

// Role options with icons and descriptions
const ROLE_OPTIONS = [
  { 
    value: 'buyer', 
    label: 'Property Buyer', 
    icon: User, 
    description: 'Access buyer resources and community features',
    showProfessionalFields: false,
    showBuyerProfessionalFields: false
  },
  { 
    value: 'developer', 
    label: 'Developer/Builder', 
    icon: Building2, 
    description: 'Build trust through transparency—manage your projects',
    showProfessionalFields: true,
    showBuyerProfessionalFields: false
  },
  { 
    value: 'professional', 
    label: 'Professional Service Provider', 
    icon: Briefcase, 
    description: 'Connect with buyers: Lawyer, CA, Architect, Consultant',
    showProfessionalFields: true,
    showBuyerProfessionalFields: false
  },
];

// Occupation options for professional service providers
const OCCUPATION_OPTIONS = [
  { value: 'lawyer', label: 'Lawyer / Advocate' },
  { value: 'chartered_accountant', label: 'Chartered Accountant (CA)' },
  { value: 'company_secretary', label: 'Company Secretary (CS)' },
  { value: 'architect', label: 'Architect' },
  { value: 'civil_engineer', label: 'Civil Engineer' },
  { value: 'structural_engineer', label: 'Structural Engineer' },
  { value: 'real_estate_consultant', label: 'Real Estate Consultant' },
  { value: 'property_valuator', label: 'Property Valuator' },
  { value: 'interior_designer', label: 'Interior Designer' },
  { value: 'financial_advisor', label: 'Financial Advisor' },
  { value: 'other', label: 'Other Professional Service' },
];

// Areas of expertise options
const EXPERTISE_OPTIONS = [
  'Real Estate Law',
  'RERA Compliance',
  'Consumer Protection',
  'Property Documentation',
  'Title Verification',
  'Construction Quality',
  'Project Finance',
  'Home Loans',
  'Tax Planning',
  'Society Formation',
  'Dispute Resolution',
  'Arbitration',
];

export default function RegistrationForm({ onRegister, onCancel, loading }) {
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Role Selection, 3: Professional Details
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    ethos_ack: false,
    // Basic info
    name: '',
    email: '',
    password: '',
    phone: '',
    // Role
    role: 'buyer',
    // Professional fields
    occupation: '',
    qualification: '',
    professional_reg_number: '',
    organization: '',
    designation: '',
    years_of_experience: '',
    areas_of_expertise: [],
    linkedin_url: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleExpertise = (expertise) => {
    setFormData(prev => ({
      ...prev,
      areas_of_expertise: prev.areas_of_expertise.includes(expertise)
        ? prev.areas_of_expertise.filter(e => e !== expertise)
        : [...prev.areas_of_expertise, expertise]
    }));
  };

  const selectedRole = ROLE_OPTIONS.find(r => r.value === formData.role);
  const showProfessionalFields = selectedRole?.showProfessionalFields;
  const showBuyerProfessionalFields = selectedRole?.showBuyerProfessionalFields;
  // Show step 3 for professional service providers OR for buyers/group admins (simplified version)
  const hasStep3 = showProfessionalFields || showBuyerProfessionalFields;

  const [errors, setErrors] = useState({});
  const validateStep = (stepNum) => {
    if (stepNum !== 1) return true;
    const e = {};
    if (!formData.name.trim()) e.name = 'Please enter your full name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) e.email = 'Please enter a valid email address';
    if (formData.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (!formData.ethos_ack) e.ethos_ack = 'Please tick "I understand" to continue';
    setErrors(e);
    if (Object.keys(e).length) { toast.error(Object.values(e)[0]); return false; }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      if (step === 2 && !hasStep3) {
        // Skip step 3 if no professional fields needed
        handleSubmit();
      } else {
        setStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async () => {
    if (loading) return;
    const submitData = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
    };
    
    try {
      await onRegister(submitData);
    } catch (err) {
      // Error handling done in parent
    }
  };

  return (
    <div className="space-y-3" data-testid="registration-form">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center gap-2 mb-3">
        {(hasStep3 ? [1, 2, 3] : [1, 2]).map((s, i, arr) => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
              {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
            </div>
            {i < arr.length - 1 && <div className={`w-8 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Basic Information */}
      {step === 1 && (
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); nextStep(); }} noValidate>
          
          <div className="space-y-1.5">
            <Label htmlFor="reg-name">Full Name *</Label>
            <Input 
              id="reg-name" 
              placeholder="Enter your full name" 
              value={formData.name} 
              onChange={(e) => updateField('name', e.target.value)} 
              data-testid="register-name"
              autoComplete="name"
              required 
            />
            {errors.name && <p className="text-xs text-rose-600" data-testid="register-name-error">{errors.name}</p>}
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="reg-email">Email Address *</Label>
            <Input 
              id="reg-email" 
              type="email" 
              placeholder="you@example.com" 
              value={formData.email} 
              onChange={(e) => updateField('email', e.target.value)} 
              data-testid="register-email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              required 
            />
            {errors.email && <p className="text-xs text-rose-600" data-testid="register-email-error">{errors.email}</p>}
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="reg-password">Password *</Label>
            <div className="relative">
              <Input 
                id="reg-password" 
                type={showPassword ? "text" : "password"} 
                placeholder="Create a password (min 6 characters)" 
                value={formData.password} 
                onChange={(e) => updateField('password', e.target.value)} 
                data-testid="register-password"
                autoComplete="new-password"
                className="pr-10" 
                required 
              />
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" 
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </Button>
            </div>
            {errors.password && <p className="text-xs text-rose-600" data-testid="register-password-error">{errors.password}</p>}
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="reg-phone">Phone Number</Label>
            <Input 
              id="reg-phone" 
              type="tel" 
              placeholder="+91 98765 43210" 
              value={formData.phone} 
              onChange={(e) => updateField('phone', e.target.value)} 
              data-testid="register-phone"
              autoComplete="tel"
            />
          </div>
          
          <IsIsntCard compact ack={formData.ethos_ack} onAckChange={(v) => updateField('ethos_ack', v)} className="p-3 sm:p-4 [&_h3]:text-base" />

          <Button type="submit" className="w-full" data-testid="next-step-1">
            Continue <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </form>
      )}

      {/* Step 2: Role Selection */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="text-center mb-2">
            <h3 className="font-semibold text-lg">Select Your Role</h3>
            <p className="text-sm text-muted-foreground">How will you use FBC?</p>
          </div>
          
          <div className="grid gap-3">
            {ROLE_OPTIONS.map((role) => {
              const Icon = role.icon;
              const isSelected = formData.role === role.value;
              return (
                <div
                  key={role.value}
                  onClick={() => updateField('role', role.value)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted hover:border-primary/50'
                  }`}
                  data-testid={`role-${role.value}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{role.label}</h4>
                      <p className="text-sm text-muted-foreground">{role.description}</p>
                    </div>
                    {isSelected && <CheckCircle2 className="h-5 w-5 text-primary" />}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={prevStep}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button className="flex-1" onClick={nextStep} disabled={loading} data-testid="next-step-2">
              {hasStep3 ? (
                <>Continue <ChevronRight className="h-4 w-4 ml-2" /></>
              ) : (
                <>{loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create Account</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Professional/Background Details */}
      {step === 3 && hasStep3 && (
        <div className="space-y-4">
          {/* For Professional Service Providers */}
          {showProfessionalFields && (
            <>
              <div className="text-center mb-2">
                <h3 className="font-semibold text-lg">Professional Details</h3>
                <p className="text-sm text-muted-foreground">
                  {formData.role === 'professional' 
                    ? 'Help buyers find and connect with your services' 
                    : 'Help us understand your background'}
                </p>
              </div>
              
              {formData.role === 'professional' && (
                <div className="space-y-1.5">
                  <Label>Type of Service *</Label>
                  <Select value={formData.occupation} onValueChange={(v) => updateField('occupation', v)}>
                    <SelectTrigger data-testid="register-occupation">
                      <SelectValue placeholder="Select your profession" />
                    </SelectTrigger>
                    <SelectContent>
                      {OCCUPATION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="space-y-1.5">
                <Label htmlFor="reg-qualification">Qualifications</Label>
                <Input 
                  id="reg-qualification" 
                  placeholder="e.g., LLB, LLM, CA, B.Arch" 
                  value={formData.qualification} 
                  onChange={(e) => updateField('qualification', e.target.value)} 
                  data-testid="register-qualification"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="reg-reg-number">Professional Registration Number</Label>
                <Input 
                  id="reg-reg-number" 
                  placeholder="e.g., Bar Council ID, ICAI Membership No." 
                  value={formData.professional_reg_number} 
                  onChange={(e) => updateField('professional_reg_number', e.target.value)} 
                  data-testid="register-reg-number"
                />
                <p className="text-xs text-muted-foreground">This helps verify your credentials</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-organization">Organization/Firm</Label>
                  <Input 
                    id="reg-organization" 
                    placeholder="Company name" 
                    value={formData.organization} 
                    onChange={(e) => updateField('organization', e.target.value)} 
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-designation">Designation</Label>
                  <Input 
                    id="reg-designation" 
                    placeholder="Your title" 
                    value={formData.designation} 
                    onChange={(e) => updateField('designation', e.target.value)} 
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="reg-experience">Years of Experience</Label>
                <Input 
                  id="reg-experience" 
                  type="number" 
                  min="0"
                  placeholder="e.g., 10" 
                  value={formData.years_of_experience} 
                  onChange={(e) => updateField('years_of_experience', e.target.value)} 
                />
              </div>
              
              <div className="space-y-1.5">
                <Label>Areas of Expertise</Label>
                <div className="flex flex-wrap gap-2">
                  {EXPERTISE_OPTIONS.map(expertise => (
                    <Badge
                      key={expertise}
                      variant={formData.areas_of_expertise.includes(expertise) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleExpertise(expertise)}
                    >
                      {expertise}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="reg-linkedin">LinkedIn Profile (Optional)</Label>
                <Input 
                  id="reg-linkedin" 
                  placeholder="https://linkedin.com/in/yourprofile" 
                  value={formData.linkedin_url} 
                  onChange={(e) => updateField('linkedin_url', e.target.value)} 
                />
              </div>
            </>
          )}

          {/* For Buyers and Buyer Group Admins - Simplified version with context */}
          {showBuyerProfessionalFields && !showProfessionalFields && (
            <>
              <div className="text-center mb-2">
                <h3 className="font-semibold text-lg">Your Background</h3>
                <p className="text-sm text-muted-foreground">Optional but helpful information</p>
              </div>
              
              {/* Context explanation */}
              <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Why we ask:</strong>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li><strong>Residents Directory</strong> — Connect with neighbors who share your profession or interests</li>
                    <li><strong>Committee Nominations</strong> — Help identify qualified candidates (CAs, Lawyers, Architects) for core committee positions like Chairman, Secretary, or Treasurer</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <div className="space-y-1.5">
                <Label htmlFor="reg-occupation-buyer">Occupation / Profession</Label>
                <Input 
                  id="reg-occupation-buyer" 
                  placeholder="e.g., Software Engineer, Doctor, CA, Lawyer, Retired" 
                  value={formData.occupation} 
                  onChange={(e) => updateField('occupation', e.target.value)} 
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="reg-qualification-buyer">Qualifications (Optional)</Label>
                <Input 
                  id="reg-qualification-buyer" 
                  placeholder="e.g., MBA, CA, LLB, M.Tech" 
                  value={formData.qualification} 
                  onChange={(e) => updateField('qualification', e.target.value)} 
                />
                <p className="text-xs text-muted-foreground">
                  Professional qualifications help in forming balanced core committees
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-organization-buyer">Organization (Optional)</Label>
                  <Input 
                    id="reg-organization-buyer" 
                    placeholder="Where you work" 
                    value={formData.organization} 
                    onChange={(e) => updateField('organization', e.target.value)} 
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-designation-buyer">Designation (Optional)</Label>
                  <Input 
                    id="reg-designation-buyer" 
                    placeholder="Your role/title" 
                    value={formData.designation} 
                    onChange={(e) => updateField('designation', e.target.value)} 
                  />
                </div>
              </div>
            </>
          )}
          
          <Separator />
          
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={prevStep}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button className="flex-1" onClick={handleSubmit} disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Account
            </Button>
          </div>
        </div>
      )}
      
      {onCancel && (
        <Button variant="link" className="w-full text-muted-foreground" onClick={onCancel}>
          Already have an account? Sign In
        </Button>
      )}
    </div>
  );
}
